import { DynamicStructuredTool } from '@langchain/core/tools';
import { z } from 'zod';
import { drishtiApi, DrishtiApiError } from './drishti-client.js';
import type { components } from './drishti-openapi.js';
import { formatToolResult } from '../types.js';
import { TTL_15M } from './utils.js';
import { logger } from '../../utils/logger.js';

/**
 * Drishti account awareness.
 *
 * Surfaces actionable warnings about the user's account — low credits, nearing
 * a rate limit, or an invalid/expired key — so the agent can inform the user
 * and adapt (e.g. lean on the MCP layer when quota is tight). Backed entirely by
 * the REST /v1/account/* endpoints, so it is useful even without MCP configured.
 */

export type AccountUsage = components['schemas']['AccountUsageResponse'];

const USAGE_TTL_MS = 60_000; // 1 min — safe to poll periodically per the API docs.

/** Absolute low-balance threshold in credits (override via env). */
function lowBalanceThreshold(): number {
  const raw = Number(process.env.DRISHTI_LOW_BALANCE_THRESHOLD);
  return Number.isFinite(raw) && raw > 0 ? raw : 50;
}

export type WarningSeverity = 'info' | 'warning' | 'critical';

export interface AccountWarning {
  severity: WarningSeverity;
  code: 'low_balance' | 'no_balance' | 'rate_limit' | 'invalid_key';
  message: string;
}

export type DrishtiApiKeyValidationResult =
  | { status: 'valid' }
  | { status: 'invalid_key' }
  | { status: 'error'; message: string };

/** Fetch the live credit/usage snapshot. Throws DrishtiApiError on failure. */
export async function getAccountUsage(): Promise<AccountUsage> {
  const { data } = await drishtiApi.get<components['schemas']['AccountUsageEnvelope']>(
    '/v1/account/usage',
    {},
    { cacheable: true, ttlMs: USAGE_TTL_MS },
  );
  return data.data;
}

export async function validateDrishtiApiKey(apiKey: string): Promise<DrishtiApiKeyValidationResult> {
  const trimmed = apiKey.trim();
  if (!trimmed) return { status: 'invalid_key' };

  try {
    await drishtiApi.get<components['schemas']['AccountUsageEnvelope']>(
      '/v1/account/usage',
      {},
      {
        cacheable: false,
        timeoutMs: 3000,
        retries: 0,
        headers: { 'X-API-Key': trimmed },
      },
    );
    return { status: 'valid' };
  } catch (error) {
    if (error instanceof DrishtiApiError && error.status === 401) {
      return { status: 'invalid_key' };
    }
    const message = error instanceof Error ? error.message : String(error);
    return { status: 'error', message };
  }
}

/** Fetch effective plan limits (loosely typed). Throws DrishtiApiError on failure. */
export async function getAccountLimits(): Promise<Record<string, unknown>> {
  const { data } = await drishtiApi.get<{ data?: Record<string, unknown> } | Record<string, unknown>>(
    '/v1/account/limits',
    {},
    { cacheable: true, ttlMs: TTL_15M },
  );
  const envelope = data as { data?: Record<string, unknown> };
  return (envelope.data ?? (data as Record<string, unknown>)) ?? {};
}

/**
 * Pure evaluation of account health into actionable warnings. Returns [] when
 * everything looks healthy. Unit-tested in isolation (no network).
 */
export function evaluateAccountHealth(usage: AccountUsage): AccountWarning[] {
  const warnings: AccountWarning[] = [];
  const balance = usage.balance ?? 0;
  const reserved = usage.reserved ?? 0;
  const available = balance - reserved;

  if (available <= 0) {
    warnings.push({
      severity: 'critical',
      code: 'no_balance',
      message: `Drishti credits exhausted (balance ${balance}, reserved ${reserved}). Requests may start failing — top up credits to continue.`,
    });
  } else if (available < lowBalanceThreshold()) {
    warnings.push({
      severity: 'warning',
      code: 'low_balance',
      message: `Drishti credits are low (${available} available). Consider topping up before running large research batches; credit-billed calls (summaries, attachment URLs) will consume the remaining balance quickly.`,
    });
  }

  // Approaching the daily rate limit: compare today's debits against the plan's
  // overall daily cap when present.
  const dailyCap = usage.rate_limits?.overall?.daily;
  const debitedToday = usage.debited_today ?? 0;
  if (typeof dailyCap === 'number' && dailyCap > 0 && debitedToday >= 0.8 * dailyCap) {
    const pct = Math.round((debitedToday / dailyCap) * 100);
    warnings.push({
      severity: debitedToday >= dailyCap ? 'critical' : 'warning',
      code: 'rate_limit',
      message: `Approaching Drishti daily limit: ${debitedToday}/${dailyCap} used today (${pct}%). Requests may be throttled — prefer cached/broad queries and expect automatic MCP fallback on rate-limit errors.`,
    });
  }

  return warnings;
}

/** Format a usage snapshot + warnings into a compact result payload. */
function snapshot(usage: AccountUsage, warnings: AccountWarning[]) {
  return {
    account_id: usage.account_id,
    balance: usage.balance,
    reserved: usage.reserved,
    debited_today: usage.debited_today,
    daily_limit: usage.rate_limits?.overall?.daily ?? null,
    rpm_limit: usage.rate_limits?.overall?.rpm ?? null,
    warnings,
  };
}

export const ACCOUNT_STATUS_DESCRIPTION = `Report the current state of the user's Drishti account: credit balance, credits used today, plan rate limits, and any actionable warnings (low credits, nearing a rate limit, or an invalid/expired API key).

Use this when the user asks about their Drishti account/credits/limits, or after you see repeated rate-limit or quota errors, so you can explain what's happening and what to do about it.`;

/** The `drishti_account_status` tool. */
export function createAccountStatusTool(): DynamicStructuredTool {
  return new DynamicStructuredTool({
    name: 'drishti_account_status',
    description: ACCOUNT_STATUS_DESCRIPTION,
    schema: z.object({}),
    func: async () => {
      try {
        const usage = await getAccountUsage();
        const warnings = evaluateAccountHealth(usage);
        return formatToolResult(snapshot(usage, warnings));
      } catch (error) {
        if (error instanceof DrishtiApiError && error.status === 401) {
          return formatToolResult({
            warnings: [invalidKeyWarning()],
          });
        }
        const message = error instanceof Error ? error.message : String(error);
        return formatToolResult({ error: `Could not fetch Drishti account status: ${message}` });
      }
    },
  });
}

export function invalidKeyWarning(): AccountWarning {
  return {
    severity: 'critical',
    code: 'invalid_key',
    message:
      'Drishti API key is invalid or expired (401 Unauthorized). Set a valid DRISHTI_API_KEY (run /keys in the CLI) — requests to Drishti will keep failing until it is fixed.',
  };
}

// --- Session preflight (best-effort, cached, non-blocking) ---

interface PreflightResult {
  warnings: AccountWarning[];
  seedText: string | null;
}

const EMPTY_PREFLIGHT: PreflightResult = { warnings: [], seedText: null };
const PREFLIGHT_CACHE_MS = 5 * 60 * 1000;
let preflightCache: { at: number; result: PreflightResult } | null = null;
let refreshInFlight = false;

function formatSeed(usage: AccountUsage, warnings: AccountWarning[]): string {
  const dailyCap = usage.rate_limits?.overall?.daily;
  const usagePart =
    typeof dailyCap === 'number' ? `; daily usage ${usage.debited_today ?? 0}/${dailyCap}` : '';
  const base = `Account: ${usage.balance ?? 0} credits${usagePart}.`;
  if (warnings.length === 0) return `${base} Healthy.`;
  return `${base}\n${warnings.map((w) => `⚠ ${w.message}`).join('\n')}`;
}

/** Refresh the cached snapshot in the background. Never throws. */
async function refreshPreflight(): Promise<void> {
  if (refreshInFlight) return;
  refreshInFlight = true;
  try {
    // Bounded and non-retrying so a slow/dead endpoint can't linger.
    const { data } = await drishtiApi.get<components['schemas']['AccountUsageEnvelope']>(
      '/v1/account/usage',
      {},
      { cacheable: true, ttlMs: USAGE_TTL_MS, timeoutMs: 3000, retries: 0 },
    );
    const usage = data.data;
    const warnings = evaluateAccountHealth(usage);
    preflightCache = { at: Date.now(), result: { warnings, seedText: formatSeed(usage, warnings) } };
  } catch (error) {
    if (error instanceof DrishtiApiError && error.status === 401) {
      const warning = invalidKeyWarning();
      preflightCache = { at: Date.now(), result: { warnings: [warning], seedText: `⚠ ${warning.message}` } };
    } else {
      const message = error instanceof Error ? error.message : String(error);
      logger.warn(`[Drishti account] preflight skipped: ${message}`);
      preflightCache = { at: Date.now(), result: EMPTY_PREFLIGHT };
    }
  } finally {
    refreshInFlight = false;
  }
}

/**
 * Best-effort account preflight for a session. NEVER blocks the agent: returns
 * the cached snapshot immediately (empty on the very first call) and kicks off a
 * background refresh when the cache is stale. So warnings appear from the second
 * turn onward, while `drishti_account_status` gives the live truth on demand.
 * Returns empty when DRISHTI_API_KEY is unset.
 */
export async function runAccountPreflight(): Promise<PreflightResult> {
  if (!process.env.DRISHTI_API_KEY) return EMPTY_PREFLIGHT;

  const fresh = preflightCache && Date.now() - preflightCache.at < PREFLIGHT_CACHE_MS;
  if (!fresh) {
    // Fire-and-forget; do not await — the current turn uses whatever we have.
    void refreshPreflight();
  }
  return preflightCache?.result ?? EMPTY_PREFLIGHT;
}

/** Test-only: clear the preflight cache. */
export function __resetAccountPreflightForTests(): void {
  preflightCache = null;
  refreshInFlight = false;
}
